import { Component } from "@angular/core";
import { GeocodingService } from "src/app/core/helpers/geocoding.service";
import { WeatherContextNormalizerService } from "src/app/core/helpers/weather-context-normalizer.service";
import { WeatherDecisionService } from "src/app/core/helpers/weather-decision.service";
import { WeatherService } from "src/app/core/helpers/weather.service";

@Component({
  selector: "loyaltic-rain",
  templateUrl: "./rain.component.html",
  styleUrls: ["./rain.component.scss"],
})
export class RainComponent {
  isWeatherProbeLoading = false;
  isRainHoursCollapsed = true;
  isWeatherSummaryCollapsed = true;
  isWeatherDetailsCollapsed = true;

  weatherProbe = {
    streetAddress: "",
    postalCode: "",
    city: "",
    date: "",
    time: ""
  };

  weatherProbeResult: any = null;

  constructor(
    private weatherContextNormalizer: WeatherContextNormalizerService,
    private geocodingService: GeocodingService,
    private weatherService: WeatherService,
    private weatherDecisionService: WeatherDecisionService
  ) {}

  async runWeatherProbe(formValues?: any) {
    if (formValues) {
      this.weatherProbe = {
        streetAddress: formValues.streetAddress || "",
        postalCode: formValues.postalCode || "",
        city: formValues.city || "",
        date: formValues.date || "",
        time: formValues.time || ""
      };
    }

    this.isWeatherProbeLoading = true;
    this.weatherProbeResult = null;
    this.isRainHoursCollapsed = true;
    this.isWeatherSummaryCollapsed = true;
    this.isWeatherDetailsCollapsed = true;

    try {
      const context = this.weatherContextNormalizer.normalize({
        receiptPartitionKey: "weather-probe",
        receiptRowKey: "weather-probe",
        merchantName: "",
        streetAddress: this.weatherProbe.streetAddress,
        postalCode: this.weatherProbe.postalCode,
        city: this.weatherProbe.city,
        country: "Sweden",
        purchaseLocalDateTime: `${this.weatherProbe.date}T${this.weatherProbe.time}:00`,
        timezone: "Europe/Stockholm",
        hasSufficientContext: true
      });

      const addressCandidates = this.weatherContextNormalizer.buildAddressCandidates(context);
      this.logRainProbe("input", {
        address: {
          streetAddress: this.weatherProbe.streetAddress,
          postalCode: this.weatherProbe.postalCode,
          city: this.weatherProbe.city,
          country: context.country,
          candidates: addressCandidates
        },
        date: this.weatherProbe.date,
        time: this.weatherProbe.time,
        purchaseLocalDateTime: context.purchaseLocalDateTime,
        timezone: context.timezone
      });

      const locationResolution = await this.geocodingService.resolve(context);

      if (locationResolution.resolutionLevel === "unresolved") {
        this.weatherProbeResult = {
          stage: "geocoding_unresolved",
          ocrContext: context,
          addressCandidates,
          geocodingAttemptedQuery: this.geocodingService.lastDebug && this.geocodingService.lastDebug.candidate,
          geocodingRawResponse: this.geocodingService.lastDebug && this.geocodingService.lastDebug.response,
          locationResolution
        };
        this.logRainProbe("geocoding unresolved", {
          address: addressCandidates,
          date: this.weatherProbe.date,
          time: this.weatherProbe.time,
          rainStatus: "unknown",
          locationResolution,
          geocodingAttemptedQuery: this.weatherProbeResult.geocodingAttemptedQuery
        });
        return;
      }

      const weatherLookup = await this.weatherService.fetchHourlyWeather(context, locationResolution);
      const decision = weatherLookup ? this.weatherDecisionService.evaluate(weatherLookup) : null;

      this.weatherProbeResult = {
        stage: weatherLookup ? "resolved" : "weather_unresolved",
        ocrContext: context,
        addressCandidates,
        geocodingAttemptedQuery: this.geocodingService.lastDebug && this.geocodingService.lastDebug.candidate,
        geocodingRawResponse: this.geocodingService.lastDebug && this.geocodingService.lastDebug.response,
        locationResolution,
        weatherRawResponse: this.weatherService.lastDebugResponse,
        weatherLookup,
        decision
      };
      this.logRainProbe("result", {
        address: {
          candidates: addressCandidates,
          geocodingAttemptedQuery: this.weatherProbeResult.geocodingAttemptedQuery,
          locationResolution
        },
        date: this.weatherProbe.date,
        time: this.weatherProbe.time,
        checkedWeatherTime: weatherLookup && weatherLookup.observationTimeLocal,
        rainStatus: decision ? decision.isRaining : "unknown",
        precipitationMm: weatherLookup && weatherLookup.precipitationMm,
        weatherCode: weatherLookup && weatherLookup.weatherCode,
        decisionReason: decision && decision.decisionReason,
        hourlyRows: this.buildNearbyHourlyRows(context.purchaseLocalDateTime, this.weatherService.lastDebugResponse),
        weatherLookup
      });
    } catch (error) {
      this.weatherProbeResult = {
        stage: "failed",
        error
      };
      this.logRainProbe("failed", {
        address: {
          streetAddress: this.weatherProbe.streetAddress,
          postalCode: this.weatherProbe.postalCode,
          city: this.weatherProbe.city
        },
        date: this.weatherProbe.date,
        time: this.weatherProbe.time,
        rainStatus: "unknown",
        error
      });
    } finally {
      this.isWeatherProbeLoading = false;
    }
  }

  getWeatherProbeRainHours(): Array<{ time: string; precipitation: number }> {
    const hourly = this.weatherProbeResult && this.weatherProbeResult.weatherRawResponse && this.weatherProbeResult.weatherRawResponse.hourly;
    if (!hourly || !hourly.time || !hourly.precipitation) {
      return [];
    }

    return hourly.time
      .map((time: string, index: number) => ({
        time: this.formatRainHour(time),
        precipitation: hourly.precipitation[index]
      }))
      .filter((entry: any) => entry.precipitation != null && entry.precipitation > 0);
  }

  toggleRainHours() {
    this.isRainHoursCollapsed = !this.isRainHoursCollapsed;
  }

  toggleWeatherSummary() {
    this.isWeatherSummaryCollapsed = !this.isWeatherSummaryCollapsed;
  }

  toggleWeatherDetails() {
    this.isWeatherDetailsCollapsed = !this.isWeatherDetailsCollapsed;
  }

  private formatRainHour(time: string): string {
    if (!time) {
      return "";
    }

    const timePart = time.split("T")[1] || time;
    return timePart.slice(0, 5);
  }

  private logRainProbe(stage: string, details: any) {
    console.groupCollapsed(`[rain-check] ${stage}`);
    console.log("address", details.address);
    console.log("date", details.date);
    console.log("time", details.time);
    console.log("rainStatus", details.rainStatus);
    if (details.hourlyRows) {
      console.table(details.hourlyRows);
    }
    console.log("details", details);
    console.groupEnd();
  }

  private buildNearbyHourlyRows(targetLocalDateTime: string, rawResponse: any): any[] {
    const hourly = rawResponse && rawResponse.hourly;
    if (!targetLocalDateTime || !hourly || !hourly.time) {
      return [];
    }

    const target = this.parseLocalDateTime(targetLocalDateTime);
    if (!target) {
      return [];
    }

    return hourly.time
      .map((time: string, index: number) => {
        const parsed = this.parseLocalDateTime(time);
        if (!parsed) {
          return null;
        }

        return {
          time,
          minutesBeforePurchase: Math.round((target.getTime() - parsed.getTime()) / 60000),
          isInLookbackWindow: parsed.getTime() <= target.getTime() && target.getTime() - parsed.getTime() <= 60 * 60 * 1000,
          precipitationMm: hourly.precipitation && hourly.precipitation[index] != null ? hourly.precipitation[index] : null,
          rainMm: hourly.rain && hourly.rain[index] != null ? hourly.rain[index] : null,
          weatherCode: this.getHourlyValue(hourly, index, "weather_code", "weatherCode"),
          temperatureC: this.getHourlyValue(hourly, index, "temperature_2m", "temperature2m"),
          conditions: this.getHourlyValue(hourly, index, "conditions", "conditions"),
          icon: this.getHourlyValue(hourly, index, "icon", "icon"),
          precipProbability: this.getHourlyValue(hourly, index, "precipprob", "precipProbability"),
          precipType: this.getHourlyValue(hourly, index, "preciptype", "precipType")
        };
      })
      .filter((row: any) => row && row.minutesBeforePurchase >= -180 && row.minutesBeforePurchase <= 180)
      .sort((left: any, right: any) => Math.abs(left.minutesBeforePurchase) - Math.abs(right.minutesBeforePurchase));
  }

  private parseLocalDateTime(value: string): Date | null {
    if (!value) {
      return null;
    }

    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?/);
    if (!match) {
      const parsed = new Date(value);
      return isNaN(parsed.getTime()) ? null : parsed;
    }

    return new Date(
      Number(match[1]),
      Number(match[2]) - 1,
      Number(match[3]),
      Number(match[4]),
      Number(match[5]),
      Number(match[6] || 0)
    );
  }

  private getHourlyValue(hourly: any, index: number, primaryName: string, fallbackName: string): any {
    const values = hourly && (hourly[primaryName] || hourly[fallbackName]);
    return values && values[index] != null ? values[index] : null;
  }
}
